import React from 'react';
import '../css/Help.css';

const Help = () => {
  return (
    <main className="help-container">
      <h1 className="help-title">Help & User Guide</h1>

      <p className="help-intro">
        This guide provides step-by-step instructions for navigating the
        <strong> TefDB platform</strong>, accessing the <b><i>Eragrostis tef</i></b> datasets,
        and using available bioinformatic tools.
        It is intended for researchers, students, breeders, and bioinformatics users
        working on <em>Eragrostis tef</em>.
      </p>

      {/* 1. Overview */}
      <section className="help-section">
        <h2>1. Platform Overview</h2>
        <p>
          <strong>TefDB</strong> is a web-based bioinformatics database developed to
          centralize resouces such as genomic, proteomic, and literature data related to
          <em> Eragrostis tef</em>.
          The platform integrates curated datasets from public repositories
          (e.g., NCBI, PubMed, UniProt, EnsemblPlants) and provides structured access,
          visualization, and analysis tools.
        </p>
      </section>

      {/* 2. Platform Layout */}
      <section className="help-section">
        <h2>2. Platform Layout & Navigation</h2>
        <ul>
          <li>
            <strong>Left Sidebar (Data Centers):</strong> Main access point for all datasets,
            organized by Literature, Genomes, Genes, Proteins, and PubChem categories.
          </li>
          <li>
            <strong>Top Header Menu:</strong> Includes Home, About, Tools, Links, FAQ,
            Help, Feedback, and Register/Login pages.
          </li>
          <li>
            <strong>Right Panel:</strong> Displays reference links and recent activity
            for quick access to updates and external resources.
          </li>
          <li>
            <strong>Footer:</strong> Provides links to Our Team, Contact Us,
            and sponsoring institutions.
          </li>
        </ul>
      </section>

      {/* 3. Data Centers */}
      <section className="help-section">
        <h2>3. Data Centers (Left Sidebar)</h2>
        <p>
          All datasets are accessible via the <strong>left sidebar</strong>.
          Each dataset includes a live badge showing the current number of records.
        </p>

        <table className="help-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Datasets</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Literature</td>
              <td>PubMed, Bookshelf, PMC</td>
              <td>Scientific articles and books related to tef research</td>
            </tr>
            <tr>
              <td>Genomes</td>
              <td>Genome Assembly, BioProject, BioSample, Nucleotide, SRA, Taxonomy</td>
              <td>Genome assemblies and sequencing datasets</td>
            </tr>
            <tr>
              <td>Genes</td>
              <td>Gene, GEO Datasets</td>
              <td>Gene and expression-related datasets</td>
            </tr>
            <tr>
              <td>Proteins</td>
              <td>UniProt Protein Groups, Protein Sequences</td>
              <td>Protein sequences datasets from both NCBI and UniProt public repositories</td>
            </tr>
            <tr>
              <td>PubChem</td>
              <td>BioAssay, Pathway, Substance</td>
              <td>Chemical, pathway, and bioassay-related data</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 4. Search */}
      <section className="help-section">
        <h2>4. Searching & Browsing Data</h2>
        <ul>
          <li>Select a dataset from the left sidebar to open its table view.</li>
          <li>Use keyword-based search fields to locate specific records (e.g., title, accession number, etc.).</li>
          <li>Apply pagination and filters to manage large datasets.</li>
          <li>Click a record to view detailed metadata and linked resources.</li>
        </ul>
      </section>

      {/* 5. Tools */}
      <section className="help-section">
        <h2>5. Integrated Bioinformatics Tools</h2>
        <ul>
          <li><strong>BLAST:</strong> Nucleotide and protein sequence similarity search.</li>
          <li><strong>Primer3Plus:</strong> PCR primer design.</li>
          <li><strong>Clustal Omega:</strong> Multiple sequence alignment.</li>
          <li><strong>JBrowse 2:</strong> Interactive genome visualization.</li>
        </ul>
      </section>

      {/* 6. Download */}
      <section className="help-section">
        <h2>6. Data Download</h2>
        <p>
          Most datasets can be downloaded or copy in standard formats including
          FASTA, genbank, genpept, CSV, and JSON. Bulk download options are available where supported.
        </p>
      </section>

      {/* 7. API */}
      <section className="help-section">
        <h2>7. API Access</h2>
        <p>
          TefDB provides RESTful API endpoints for programmatic access to all datasets.
          All responses are returned in JSON format.
        </p>

        <pre className="help-code">
{`/api/gene/
 /api/nucleotide/
 /api/taxonomy/
 /api/genomeassembly/
 /api/pubmed/
 /api/book/
 /api/pmc/
 /api/geo/
 /api/protein/
 /api/bioassay/
 /api/pathway/
 /api/substance/
 /api/sra/
 /api/bioproject/
 /api/biosample/`}
        </pre>
      </section>

      {/* 8. Submission */}
      <section className="help-section">
        <h2>8. Data Submission</h2>
        <ul>
          <li>The <strong>Data Submission</strong> button is located in the left sidebar, under the data center.</li>
          <li>User authentication is required to submit data.</li>
          <li>All submitted datasets are reviewed before publication.</li>
        </ul>
      </section>

      {/* 9. Support */}
      <section className="help-section">
        <h2>9. Support & Contact</h2>
        <ul>
          <li>
            <strong>Email:</strong>{' '}
            <a href="mailto:badofirdo11@gmail.com">badofirdo11@gmail.com</a>
          </li>
          <li>
            <strong>Telegram:</strong>{' '}
            <a href="https://t.me/badof1" target="_blank" rel="noopener noreferrer">
              https://t.me/badof1
            </a>
          </li>
          <li>
            <strong>WhatsApp:</strong>{' '}
            <a href="https://wa.me/251933992667" target="_blank" rel="noopener noreferrer">
              +251 933 992 667
            </a>
          </li>
          <li>
            Questions, comments, and suggestions can also be submitted via the{' '}
            <strong>
              <a href="http://197.156.76.122/contact_form" target="_blank" rel="noopener noreferrer">
                Feedback
              </a>
            </strong>{' '}
            page, or you can contact team members directly on the{' '}
            <strong>
              <a href="http://197.156.76.122/team" target="_blank" rel="noopener noreferrer">
                Team
              </a>
            </strong>{' '}
            page.
          </li>
        </ul>
      </section>

      <div className="help-footer">
        Questions, comments, and suggestions can also be submitted via the{' '}
        <strong>
          <a href="http://197.156.76.122/contact_form" target="_blank" rel="noopener noreferrer">
            Feedback
          </a>
        </strong>{' '}
        page, or by reaching out to team members on the{' '}
        <strong>
          <a href="http://197.156.76.122/team" target="_blank" rel="noopener noreferrer">
            Team
          </a>
        </strong>{' '}
        page.
      </div>
    </main>
  );
};

export default Help;

